import React, { useState, useEffect } from 'react';
import { Calendar, Tag, Play, FileText, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../components/ui/accordion';
import { Separator } from '../components/ui/separator';
import Layout from '../components/layout/Layout';
import { faq, publications as mockPublications } from '../data/mock';
import { getPublications } from '../services/api';

const PublicationsPage = () => {
  const [publications, setPublications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPublications = async () => {
      try {
        const data = await getPublications();
        // If no publications in DB, use mock data
        setPublications(data.length > 0 ? data : mockPublications);
      } catch (err) {
        console.error('Error fetching publications:', err);
        // Fallback to mock data on error
        setPublications(mockPublications);
        setError('Utilisation des données locales');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPublications();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-light text-gray-900">
              Publications et ressources
            </h1>
            <p className="mt-6 text-xl text-gray-600 leading-relaxed">
              Articles éducatifs et informations médicales pour mieux comprendre les pathologies chirurgicales et leurs traitements.
            </p>
          </div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-10">
            <FileText className="w-6 h-6 text-teal-700" />
            <h2 className="text-2xl font-medium text-gray-900">
              Articles récents
            </h2>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-teal-700 animate-spin" />
              <span className="ml-3 text-gray-600">Chargement des articles...</span>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {publications.map((article) => (
                <Card
                  key={article.id}
                  className="border border-gray-100 shadow-sm hover:shadow-md hover:border-teal-100 transition-all duration-300 cursor-pointer group"
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="secondary" className="bg-teal-50 text-teal-700 hover:bg-teal-100">
                        <Tag className="w-3 h-3 mr-1" />
                        {article.category}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-medium text-gray-900 group-hover:text-teal-700 transition-colors">
                      {article.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(article.date || article.created_at)}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Video Section Placeholder */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-10">
            <Play className="w-6 h-6 text-teal-700" />
            <h2 className="text-2xl font-medium text-gray-900">
              Vidéos éducatives
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="border border-gray-200 shadow-sm">
                <div className="aspect-video bg-gray-200 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-white/80 flex items-center justify-center">
                    <Play className="w-8 h-8 text-gray-400 ml-1" />
                  </div>
                </div>
                <CardContent className="pt-4">
                  <p className="text-gray-500 text-sm">
                    Contenu vidéo à venir
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="mt-8 text-sm text-gray-500 text-center">
            De nouvelles vidéos pédagogiques seront bientôt disponibles.
          </p>
        </div>
      </section>

      <Separator className="max-w-7xl mx-auto" />

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-medium text-gray-900 mb-10 text-center">
              Questions fréquentes
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {faq.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left text-gray-900 hover:text-teal-700">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-sm text-gray-500">
              Les informations présentées sur cette page sont à caractère éducatif et ne remplacent pas une consultation médicale. Chaque situation est unique et nécessite un avis médical personnalisé.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PublicationsPage;
